import { Badge, Select, TextInput } from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import classes from './lots-validation.module.css';
import { LotsQuickFilter, useLotsFilters } from './use-lots-filters';

export interface LotsFilterToolbarProps {
  years: number[];
  species: { value: string; label: string }[];
}

export const LotsFilterToolbar: FC<LotsFilterToolbarProps> = ({
  years,
  species,
}) => {
  const { t } = useTranslation();
  const {
    searchInput,
    setSearchInput,
    year,
    speciesId,
    statusValue,
    quickFilters,
    toggleQuickFilter,
    handleYearChange,
    handleSpeciesChange,
    handleStatusChange,
  } = useLotsFilters();

  const quickFilterDefs: { key: LotsQuickFilter; label: string; pending?: boolean }[] =
    [
      { key: 'pending', label: t('lots.filters.pill_pending'), pending: true },
      { key: 'tonnage_delta', label: t('lots.filters.pill_tonnage_delta') },
      {
        key: 'with_calculated_area',
        label: t('lots.filters.pill_with_calculated_area'),
      },
    ];

  return (
    <div className={classes.filtersRow}>
      <div className={classes.toolbarFilters}>
        <TextInput
          className={classes.searchInput}
          leftSection={<IconSearch size={14} stroke={1.5} />}
          onChange={(e) => setSearchInput(e.currentTarget.value)}
          placeholder={t('lots.filters.search_placeholder')}
          size="xs"
          value={searchInput}
        />
        <Select
          className={classes.filterSelect}
          clearable
          comboboxProps={{ withinPortal: true }}
          data={years.map((y) => ({
            value: y.toString(),
            label: y.toString(),
          }))}
          onChange={handleYearChange}
          placeholder={t('lots.filters.year')}
          size="xs"
          value={year}
        />
        <Select
          className={classes.filterSelect}
          clearable
          comboboxProps={{ withinPortal: true }}
          data={species}
          onChange={handleSpeciesChange}
          placeholder={t('lots.filters.species')}
          size="xs"
          value={speciesId}
        />
        <Select
          className={classes.filterSelect}
          clearable
          comboboxProps={{ withinPortal: true }}
          data={[
            { value: 'false', label: t('lots.status.pending') },
            { value: 'true', label: t('lots.status.validated') },
          ]}
          onChange={handleStatusChange}
          placeholder={t('lots.filters.status')}
          size="xs"
          value={statusValue}
        />
      </div>
      <div className={classes.toolbarPills}>
        {quickFilterDefs.map(({ key, label, pending }) => {
          const active = quickFilters.has(key);
          const color = active ? (pending ? 'orange' : 'pf-green') : 'gray';
          return (
            <Badge
              className={classes.pill}
              color={color}
              key={key}
              onClick={() => toggleQuickFilter(key)}
              radius="xl"
              size="md"
              variant={active ? 'filled' : 'light'}
            >
              {label}
            </Badge>
          );
        })}
      </div>
    </div>
  );
};

export default LotsFilterToolbar;
